import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import {
  buildManualGrantPayload,
  EMPTY_MANUAL_GRANT_FORM,
  type ManualGrantFormState,
} from '../lib/manual_grant';

// ── Types matching the API response shape ────────────────────────────────

interface NeedsReviewItem {
  id: string;
  title: string;
  source_url: string;
  funder_id: string;
  funder_name: string;
  ingested_at: number;
  closes_at: number | null;
  intake_type: string | null;
}

interface GrantDetail {
  id: string;
  funder_id: string;
  funder_name: string;
  funder_level: string;
  external_program_code: string | null;
  title: string;
  source_url: string;
  raw_object_key: string | null;
  description: string | null;
  eligible_applicants: string[];
  eligible_activities: string[];
  geographic_scope: string[];
  sectors: string[];
  funding_min_cad: number | null;
  funding_max_cad: number | null;
  match_required: boolean;
  match_ratio: number | null;
  application_complexity: 'light' | 'medium' | 'heavy' | null;
  intake_type: 'continuous' | 'window' | 'invitational' | 'closed' | null;
  opens_at: number | null;
  closes_at: number | null;
  decision_by: number | null;
  source_quotes: Record<string, string>;
  indigenous_eligible: boolean;
  indigenous_required: boolean;
  verification_status: 'needs_review' | 'verified' | 'rejected' | 'superseded';
}

interface VerifyResponse {
  id: string;
  decision: 'approve' | 'reject' | 'save';
  verification_status: GrantDetail['verification_status'];
  vector_id?: string;
  matching_triggered?: boolean;
}

// ── Page ─────────────────────────────────────────────────────────────────

export default function VerifyPage() {
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: ['admin', 'grants', 'needs-review'],
    queryFn: () => api.get<NeedsReviewItem[]>('/admin/grants/needs-review'),
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  // Auto-select first item once list loads.
  useEffect(() => {
    if (!selectedId && list.data?.data?.length) setSelectedId(list.data.data[0].id);
  }, [list.data, selectedId]);

  if (list.isLoading) return <Padded>Loading queue…</Padded>;
  if (list.error instanceof ApiError) {
    if (list.error.status === 401) {
      navigate({ to: '/login' });
      return null;
    }
    return <Padded>Error loading queue: {list.error.message}</Padded>;
  }
  const items = list.data?.data ?? [];

  return (
    <div className="grid h-full grid-cols-[320px_1fr] border-t border-neutral-200">
      {/* Left rail — the queue */}
      <aside className="border-r border-neutral-200 overflow-y-auto">
        <div className="p-3 border-b border-neutral-200 flex items-center justify-between">
          <span className="text-xs uppercase tracking-wide text-neutral-500">
            Needs review ({items.length})
          </span>
          <button
            type="button"
            onClick={() => { setAdding(true); setSelectedId(null); }}
            className="text-xs rounded-md border border-neutral-300 px-2 py-1 hover:bg-neutral-50"
            title="Enter a grant you heard about outside the scrapers"
          >+ Add grant</button>
        </div>
        {items.length === 0 ? (
          <div className="p-4 text-sm text-neutral-600">
            Queue is empty. Run a source from Admin → Sources, or use “+ Add grant”
            to enter one you heard about directly.
          </div>
        ) : (
          <ul role="listbox" aria-label="Grants to review">
            {items.map((it) => (
              <li key={it.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selectedId === it.id}
                  onClick={() => setSelectedId(it.id)}
                  className={
                    'w-full text-left px-3 py-2 border-b border-neutral-100 ' +
                    (selectedId === it.id
                      ? 'bg-accent/5 border-l-2 border-l-accent'
                      : 'hover:bg-neutral-50')
                  }
                >
                  <div className="text-sm font-medium text-neutral-900 truncate">{it.title}</div>
                  <div className="text-xs text-neutral-500 truncate">{it.funder_name}</div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>

      {/* Right pane — add form, or split view (raw + form) */}
      {adding ? (
        <ManualAddForm
          onCreated={(id) => { setAdding(false); setSelectedId(id); }}
          onCancel={() => setAdding(false)}
        />
      ) : selectedId ? (
        <VerifyDetail id={selectedId} onDone={() => setSelectedId(null)} />
      ) : (
        <Padded>Select a grant on the left to review it, or “+ Add grant” to enter one manually.</Padded>
      )}
    </div>
  );
}

// ── Detail (split view) ──────────────────────────────────────────────────

function VerifyDetail({ id, onDone }: { id: string; onDone: () => void }) {
  const qc = useQueryClient();
  const grant = useQuery({
    queryKey: ['admin', 'grant', id],
    queryFn: () => api.get<GrantDetail>(`/admin/grants/${id}`),
  });

  // Local form state — populated from API, then user-editable.
  const [form, setForm] = useState<Partial<GrantDetail> | null>(null);
  const [notes, setNotes] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (grant.data?.data) setForm(grant.data.data);
    setNotes('');
    setFeedback(null);
    setError(null);
  }, [grant.data]);

  const verify = useMutation({
    mutationFn: (decision: 'approve' | 'reject' | 'save') => {
      const edits = form ? diffEdits(grant.data!.data, form) : undefined;
      return api.patch<VerifyResponse>(`/admin/grants/${id}/verify`, {
        decision,
        ...(edits && Object.keys(edits).length > 0 ? { edits } : {}),
        ...(notes ? { notes } : {}),
      });
    },
    onSuccess: async (res, decision) => {
      setError(null);
      if (decision === 'approve') {
        setFeedback(`Approved. Vectorize id: ${res.data.vector_id ?? '—'}.`);
      } else if (decision === 'reject') {
        setFeedback('Rejected.');
      } else {
        setFeedback('Saved.');
      }
      await qc.invalidateQueries({ queryKey: ['admin', 'grants', 'needs-review'] });
      await qc.invalidateQueries({ queryKey: ['admin', 'grant', id] });
      if (decision !== 'save') onDone();
    },
    onError: (err) => {
      setFeedback(null);
      setError(err instanceof ApiError ? `${err.code}: ${err.message}` : String(err));
    },
  });

  if (grant.isLoading || !form) return <Padded>Loading grant…</Padded>;
  if (grant.error) return <Padded>Error: {(grant.error as Error).message}</Padded>;
  const g = grant.data!.data;

  return (
    <section className="grid grid-cols-2 h-full min-h-0">
      {/* Raw source from R2, sandboxed */}
      <div className="border-r border-neutral-200 flex flex-col min-h-0">
        <div className="px-3 py-2 border-b border-neutral-200 text-xs uppercase tracking-wide text-neutral-500 flex items-center gap-3">
          <span>Source</span>
          <a
            href={g.source_url}
            target="_blank"
            rel="noreferrer noopener"
            className="text-accent underline normal-case tracking-normal text-xs"
          >
            open on funder site ↗
          </a>
        </div>
        {g.raw_object_key ? (
          <iframe
            title="Raw source"
            src={`/api/v1/admin/grants/${id}/source`}
            sandbox=""
            className="flex-1 min-h-0 w-full"
          />
        ) : (
          <div className="flex-1 min-h-0 p-4 text-sm text-neutral-600 bg-neutral-50">
            <p className="font-medium text-neutral-800">Manually entered grant</p>
            <p className="mt-1">
              There is no scraped snapshot for this record — it was typed in, not
              ingested. Check the details against the funder page before approving:
            </p>
            <a
              href={g.source_url}
              target="_blank"
              rel="noreferrer noopener"
              className="mt-2 inline-block text-accent underline"
            >open on funder site ↗</a>
          </div>
        )}
      </div>

      {/* Parsed fields — editable */}
      <div className="overflow-y-auto">
        <header className="px-4 py-3 border-b border-neutral-200">
          <h2 className="text-base font-semibold text-neutral-900 truncate">{g.title}</h2>
          <p className="text-xs text-neutral-500">
            {g.funder_name} · {g.funder_level} ·{' '}
            <span className="uppercase tracking-wide">{g.verification_status}</span>
          </p>
        </header>

        <div className="p-4 space-y-4 text-sm">
          <Field label="Title" quote={g.source_quotes.title}>
            <input
              className={inputCls}
              value={form.title ?? ''}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </Field>

          <Field label="Description" quote={g.source_quotes.description}>
            <textarea
              className={inputCls + ' min-h-[80px]'}
              value={form.description ?? ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Funding min (CAD)">
              <NumberInput value={form.funding_min_cad ?? null} onChange={(v) => setForm({ ...form, funding_min_cad: v })} />
            </Field>
            <Field label="Funding max (CAD)" quote={g.source_quotes.funding_max_cad}>
              <NumberInput value={form.funding_max_cad ?? null} onChange={(v) => setForm({ ...form, funding_max_cad: v })} />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Intake type" quote={g.source_quotes.intake_type}>
              <select
                className={inputCls}
                value={form.intake_type ?? 'continuous'}
                onChange={(e) => setForm({ ...form, intake_type: e.target.value as GrantDetail['intake_type'] })}
              >
                <option value="continuous">continuous</option>
                <option value="window">window</option>
                <option value="invitational">invitational</option>
                <option value="closed">closed</option>
              </select>
            </Field>
            <Field label="Complexity">
              <select
                className={inputCls}
                value={form.application_complexity ?? 'medium'}
                onChange={(e) => setForm({ ...form, application_complexity: e.target.value as GrantDetail['application_complexity'] })}
              >
                <option value="light">light</option>
                <option value="medium">medium</option>
                <option value="heavy">heavy</option>
              </select>
            </Field>
          </div>

          <Field label="Sectors" quote={g.source_quotes.sectors}>
            <StringListInput
              value={form.sectors ?? []}
              onChange={(v) => setForm({ ...form, sectors: v })}
            />
          </Field>

          <Field label="Eligible applicants" quote={g.source_quotes.eligible_applicants}>
            <StringListInput
              value={form.eligible_applicants ?? []}
              onChange={(v) => setForm({ ...form, eligible_applicants: v })}
            />
          </Field>

          <Field label="Geographic scope" quote={g.source_quotes.geographic_scope}>
            <StringListInput
              value={form.geographic_scope ?? []}
              onChange={(v) => setForm({ ...form, geographic_scope: v })}
            />
          </Field>

          <div className="flex items-center gap-4">
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.indigenous_required ?? false}
                onChange={(e) => setForm({ ...form, indigenous_required: e.target.checked })}
              />
              <span>Indigenous-required</span>
            </label>
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.indigenous_eligible ?? false}
                onChange={(e) => setForm({ ...form, indigenous_eligible: e.target.checked })}
              />
              <span>Indigenous-eligible</span>
            </label>
          </div>

          <Field label="Reviewer notes (optional)">
            <textarea
              className={inputCls + ' min-h-[60px]'}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Why approve / reject / save?"
            />
          </Field>

          {feedback && (
            <div role="status" aria-live="polite" className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-2">
              {feedback}
            </div>
          )}
          {error && (
            <div role="alert" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-2">
              {error}
            </div>
          )}
        </div>

        <footer className="sticky bottom-0 bg-white border-t border-neutral-200 p-3 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => verify.mutate('save')}
            disabled={verify.isPending}
            className="px-3 py-1.5 rounded-md border border-neutral-300 text-sm hover:bg-neutral-50 disabled:opacity-60"
          >Save</button>
          <button
            type="button"
            onClick={() => verify.mutate('reject')}
            disabled={verify.isPending}
            className="px-3 py-1.5 rounded-md border border-rose-300 text-rose-700 text-sm hover:bg-rose-50 disabled:opacity-60"
          >Reject</button>
          <button
            type="button"
            onClick={() => verify.mutate('approve')}
            disabled={verify.isPending}
            className="px-3 py-1.5 rounded-md bg-accent text-white text-sm hover:bg-accent-hover disabled:opacity-60"
          >{verify.isPending ? 'Working…' : 'Approve'}</button>
        </footer>
      </div>
    </section>
  );
}

// ── Manual add form ──────────────────────────────────────────────────────
//
// Word-of-mouth grants (reports/01 item 10). Submits to POST /admin/grants;
// the new row lands in this same needs-review queue and is selected for
// immediate review, so the human gate still applies to manual entries.

interface FunderOption { id: string; name: string; level: string }

function ManualAddForm({ onCreated, onCancel }: { onCreated: (id: string) => void; onCancel: () => void }) {
  const qc = useQueryClient();
  const funders = useQuery({
    queryKey: ['admin', 'funders'],
    queryFn: () => api.get<FunderOption[]>('/admin/funders'),
  });
  const [form, setForm] = useState<ManualGrantFormState>(EMPTY_MANUAL_GRANT_FORM);
  const [errors, setErrors] = useState<string[]>([]);

  const create = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.post<{ id: string; verification_status: string }>('/admin/grants', payload),
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ['admin', 'grants', 'needs-review'] });
      onCreated(res.data.id);
    },
    onError: (err) => {
      setErrors([err instanceof ApiError ? `${err.code}: ${err.message}` : String(err)]);
    },
  });

  function submit() {
    const built = buildManualGrantPayload(form);
    if (!built.ok) { setErrors(built.errors); return; }
    setErrors([]);
    create.mutate(built.payload);
  }

  return (
    <section className="overflow-y-auto">
      <header className="px-4 py-3 border-b border-neutral-200">
        <h2 className="text-base font-semibold text-neutral-900">Add a grant manually</h2>
        <p className="text-xs text-neutral-500">
          For grants you heard about outside the scrapers. It will land in the
          review queue as “needs review” — same verification step as everything else.
        </p>
      </header>

      <div className="p-4 space-y-4 text-sm max-w-xl">
        <Field label="Funder">
          <select
            className={inputCls}
            value={form.funder_id}
            onChange={(e) => setForm({ ...form, funder_id: e.target.value })}
          >
            <option value="">{funders.isLoading ? 'Loading funders…' : 'Pick a funder…'}</option>
            {(funders.data?.data ?? []).map((f) => (
              <option key={f.id} value={f.id}>{f.name} ({f.level})</option>
            ))}
          </select>
        </Field>

        <Field label="Title">
          <input
            className={inputCls}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Community Enhancement Program"
          />
        </Field>

        <Field label="Source URL (the funder page you read)">
          <input
            className={inputCls}
            value={form.source_url}
            onChange={(e) => setForm({ ...form, source_url: e.target.value })}
            placeholder="https://…"
            inputMode="url"
          />
        </Field>

        <Field label="Description">
          <textarea
            className={inputCls + ' min-h-[80px]'}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="What is this funding for, in a sentence or two?"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Funding min (CAD, optional)">
            <input
              className={inputCls}
              value={form.funding_min_cad}
              onChange={(e) => setForm({ ...form, funding_min_cad: e.target.value })}
              inputMode="numeric"
              placeholder="e.g. 50000"
            />
          </Field>
          <Field label="Funding max (CAD, optional)">
            <input
              className={inputCls}
              value={form.funding_max_cad}
              onChange={(e) => setForm({ ...form, funding_max_cad: e.target.value })}
              inputMode="numeric"
              placeholder="e.g. 200000"
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Deadline (optional)">
            <input
              type="date"
              className={inputCls}
              value={form.closes_at_date}
              onChange={(e) => setForm({ ...form, closes_at_date: e.target.value })}
            />
          </Field>
          <Field label="Intake type (optional)">
            <select
              className={inputCls}
              value={form.intake_type}
              onChange={(e) => setForm({ ...form, intake_type: e.target.value as ManualGrantFormState['intake_type'] })}
            >
              <option value="">unknown</option>
              <option value="continuous">continuous</option>
              <option value="window">window</option>
              <option value="invitational">invitational</option>
              <option value="closed">closed</option>
            </select>
          </Field>
        </div>

        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.match_required}
            onChange={(e) => setForm({ ...form, match_required: e.target.checked })}
          />
          <span>Matching funds required</span>
        </label>

        {errors.length > 0 && (
          <div role="alert" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-2">
            <ul className="list-disc pl-4">
              {errors.map((e) => <li key={e}>{e}</li>)}
            </ul>
          </div>
        )}

        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            onClick={submit}
            disabled={create.isPending}
            className="px-3 py-1.5 rounded-md bg-accent text-white text-sm hover:bg-accent-hover disabled:opacity-60"
          >{create.isPending ? 'Creating…' : 'Create — goes to review'}</button>
          <button
            type="button"
            onClick={onCancel}
            disabled={create.isPending}
            className="px-3 py-1.5 rounded-md border border-neutral-300 text-sm hover:bg-neutral-50 disabled:opacity-60"
          >Cancel</button>
        </div>
      </div>
    </section>
  );
}

// ── Field primitives ─────────────────────────────────────────────────────

const inputCls =
  'w-full border border-neutral-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-accent';

function Field({
  label, quote, children,
}: { label: string; quote?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-xs uppercase tracking-wide text-neutral-600">{label}</span>
        {quote ? (
          <details className="text-xs text-neutral-500">
            <summary className="cursor-pointer hover:text-neutral-700">source quote</summary>
            <blockquote className="mt-1 font-mono whitespace-pre-wrap border-l-2 border-neutral-300 pl-2 text-neutral-700">
              {quote}
            </blockquote>
          </details>
        ) : null}
      </div>
      {children}
    </label>
  );
}

function NumberInput({ value, onChange }: { value: number | null; onChange: (v: number | null) => void }) {
  return (
    <input
      type="number"
      className={inputCls}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
    />
  );
}

function StringListInput({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  // Edit as one-per-line; trim empties on serialize. Simple, debuggable, and
  // mirrors what the API expects.
  return (
    <textarea
      className={inputCls + ' min-h-[60px] font-mono text-xs'}
      value={value.join('\n')}
      onChange={(e) => onChange(e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))}
    />
  );
}

function Padded({ children }: { children: React.ReactNode }) {
  return <div className="p-6 text-sm text-neutral-700">{children}</div>;
}

// ── Diff helper — only send changed fields ───────────────────────────────

function diffEdits(orig: GrantDetail, form: Partial<GrantDetail>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(form) as Array<keyof GrantDetail>) {
    const a = orig[key];
    const b = form[key];
    if (Array.isArray(a) || Array.isArray(b)) {
      const aa = Array.isArray(a) ? a : [];
      const bb = Array.isArray(b) ? b : [];
      if (aa.length !== bb.length || aa.some((x, i) => x !== bb[i])) out[key] = bb;
    } else if (a !== b && key !== 'source_quotes' && key !== 'funder_name' && key !== 'funder_level' && key !== 'raw_object_key' && key !== 'verification_status' && key !== 'id') {
      out[key] = b;
    }
  }
  return out;
}
